import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import {
    FileText,
    Download,
    Clock,
    Search,
    FolderOpen,
    X,
    ExternalLink,
    Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';

interface Doc {
    id: string;
    title: string;
    category: string;
    file_url: string;
    created_at: string;
}

/** Fetches a document via our authenticated API and returns a blob object URL */
async function fetchDocBlob(fileUrl: string): Promise<string> {
    // fileUrl is like http://localhost:8001/api/v1/mitra/download?path=...
    // We call it through axios so the auth header is attached automatically
    const token = localStorage.getItem('token');
    const resp = await fetch(fileUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        redirect: 'follow',
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
}

const DocumentVault: React.FC = () => {
    const [docs, setDocs] = useState<Doc[]>([]);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('All');
    const [viewer, setViewer] = useState<Doc | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const prevBlobUrl = useRef<string | null>(null);

    useEffect(() => {
        api.get('/documents').then(res => setDocs(res.data));
    }, []);

    // When a doc is selected, fetch its blob
    useEffect(() => {
        // Revoke the previous blob URL to free memory
        if (prevBlobUrl.current) {
            URL.revokeObjectURL(prevBlobUrl.current);
            prevBlobUrl.current = null;
        }
        setBlobUrl(null);
        setError(null);
        if (!viewer) return;

        setLoading(true);
        fetchDocBlob(viewer.file_url)
            .then(url => {
                prevBlobUrl.current = url;
                setBlobUrl(url);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [viewer]);

    const closeViewer = () => setViewer(null);

    const categories = ['All', ...Array.from(new Set(docs.map(d => d.category)))];

    const filtered = docs.filter(d => {
        const matchesSearch = d.title.toLowerCase().includes(search.toLowerCase());
        const matchesCat = category === 'All' || d.category === category;
        return matchesSearch && matchesCat;
    });

    return (
        <>
            <div className="space-y-8 max-w-4xl mx-auto">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Resident Handbook</h1>
                    <p className="text-slate-500 mt-1">Bye Laws, House Rules, and official society documents</p>
                </div>

                <div className="flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search documents..."
                            className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-12 pr-4 shadow-sm focus:ring-2 focus:ring-violet-500 outline-none transition-all"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex bg-slate-100 p-1 rounded-lg w-full md:w-auto overflow-x-auto scrollbar-hide">
                        {categories.map(c => (
                            <button
                                key={c}
                                onClick={() => setCategory(c)}
                                className={cn(
                                    "px-4 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all",
                                    category === c ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                                )}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((d) => (
                        <div
                            key={d.id}
                            className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-violet-300 transition-all group cursor-pointer"
                            onClick={() => setViewer(d)}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center text-violet-600 group-hover:bg-violet-600 group-hover:text-white transition-all">
                                    <FileText size={20} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                    {d.category}
                                </span>
                            </div>
                            <h4 className="font-bold text-slate-900 line-clamp-2 leading-snug mb-6">{d.title}</h4>
                            <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-slate-400">
                                    <Clock size={12} />
                                    <span className="text-[10px] font-bold uppercase">{new Date(d.created_at).toLocaleDateString()}</span>
                                </div>
                                <button
                                    onClick={e => { e.stopPropagation(); setViewer(d); }}
                                    className="flex items-center gap-1.5 text-violet-600 hover:text-violet-700 font-bold text-xs"
                                >
                                    <FileText size={14} />
                                    View
                                </button>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full py-20 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                            <FolderOpen size={40} className="text-slate-200 mx-auto mb-3" />
                            <p className="text-slate-500 font-medium">No documents found in this category</p>
                        </div>
                    )}
                </div>
            </div>

            {/* PDF Viewer Modal */}
            {viewer && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={closeViewer}
                >
                    <div
                        className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl"
                        style={{ height: '90vh' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText size={16} className="text-violet-600" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-slate-900 truncate">{viewer.title}</p>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{viewer.category}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                                <a
                                    href={viewer.file_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-violet-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-violet-50"
                                >
                                    <ExternalLink size={14} />
                                    Open in tab
                                </a>
                                {blobUrl && (
                                    <a
                                        href={blobUrl}
                                        download={`${viewer.title}.pdf`}
                                        className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-violet-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-violet-50"
                                    >
                                        <Download size={14} />
                                        Download
                                    </a>
                                )}
                                <button
                                    onClick={closeViewer}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* PDF content */}
                        <div className="flex-1 overflow-hidden rounded-b-2xl bg-slate-50 flex items-center justify-center">
                            {loading && (
                                <div className="flex flex-col items-center gap-3 text-slate-400">
                                    <Loader2 size={32} className="animate-spin text-violet-500" />
                                    <p className="text-sm font-medium">Loading document…</p>
                                </div>
                            )}
                            {error && !loading && (
                                <div className="flex flex-col items-center gap-3 text-slate-400 p-8 text-center">
                                    <FileText size={40} className="text-slate-200" />
                                    <p className="text-sm font-medium text-slate-500">Could not load document</p>
                                    <a
                                        href={viewer.file_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs font-bold text-violet-600 hover:underline"
                                    >
                                        Open in new tab instead →
                                    </a>
                                </div>
                            )}
                            {blobUrl && !loading && (
                                <iframe
                                    src={`${blobUrl}#toolbar=1&navpanes=0`}
                                    title={viewer.title}
                                    className="w-full h-full border-0 rounded-b-2xl"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DocumentVault;
